/**
 * FeedingPlanEditor — structured slot-row editor for a pet's feeding plan
 * (migration 0101). Sibling of `MedicationEditor`.
 *
 * Each row: slot (+ time for custom / optional otherwise), quantity, unit,
 * food, supplements, notes. Below the rows: owner-supplied toggle, allergies
 * and red flags. Pure controlled component — the caller owns the draft and
 * the save call (staff pet page, booking override, customer portal, /book
 * wizard intake step all reuse it).
 *
 * `compact` trims the row to slot / quantity / unit / food for the wizard.
 */
import {
  FEEDING_SLOTS,
  FEEDING_SLOT_LABELS,
  FEEDING_UNITS,
  FEEDING_UNIT_LABELS,
  type FeedingPlanDraft,
  type FeedingPlanItem,
  type FeedingSlot,
  type FeedingUnit,
  newFeedingItem,
} from '@/lib/feeding';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';

const inputCls =
  'h-8 w-full rounded border border-border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring/30 min-w-0';
const labelCls =
  'mb-0.5 block text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground';

export interface FeedingPlanEditorProps {
  value: FeedingPlanDraft;
  onChange: (value: FeedingPlanDraft) => void;
  /** Wizard mode: fewer columns, no supplements/notes row. */
  compact?: boolean;
  /** Prefix for aria labels when several editors share a page (multi-pet). */
  idPrefix?: string;
}

export function FeedingPlanEditor({
  value,
  onChange,
  compact = false,
  idPrefix = 'feeding',
}: FeedingPlanEditorProps) {
  function setItems(items: FeedingPlanItem[]) {
    onChange({ ...value, items });
  }
  function addRow() {
    const used = new Set(value.items.map((it) => it.slot));
    const nextSlot = FEEDING_SLOTS.find((s) => s !== 'custom' && !used.has(s)) ?? 'custom';
    const item = newFeedingItem(nextSlot);
    if (nextSlot === 'custom') item.time = '12:00';
    // Copy food + unit from the last row — most pets eat the same thing twice.
    const last = value.items[value.items.length - 1];
    if (last) {
      item.food = last.food;
      item.unit = last.unit;
      item.quantity = last.quantity;
    }
    setItems([...value.items, item]);
  }
  function removeRow(idx: number) {
    setItems(value.items.filter((_, i) => i !== idx));
  }
  /** Patch a row; `undefined` values DELETE the optional key (time / supplements / notes). */
  function updateRow(
    idx: number,
    patch: { [K in keyof FeedingPlanItem]?: FeedingPlanItem[K] | undefined },
  ) {
    setItems(
      value.items.map((it, i) => {
        if (i !== idx) return it;
        const next: FeedingPlanItem = { ...it };
        for (const [k, v] of Object.entries(patch) as Array<
          [keyof FeedingPlanItem, FeedingPlanItem[keyof FeedingPlanItem] | undefined]
        >) {
          if (v === undefined) delete next[k as 'time' | 'supplements' | 'notes'];
          else (next as unknown as Record<string, unknown>)[k] = v;
        }
        return next;
      }),
    );
  }

  return (
    <div className="space-y-3" data-testid={`${idPrefix}-editor`}>
      {value.items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-secondary/20 px-4 py-3 text-center">
          <p className="mb-2 text-xs text-muted-foreground">No meals on the plan yet.</p>
          <button
            type="button"
            onClick={addRow}
            className="press inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium hover:bg-secondary"
          >
            <Plus className="size-3" strokeWidth={2} />
            Add meal
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {value.items.map((item, idx) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no stable id until saved; same as MedicationEditor
            <div
              key={`${idPrefix}-row-${idx}`}
              className="rounded-md border border-border bg-background px-3 py-2"
              data-testid={`${idPrefix}-row`}
            >
              <div
                className={`grid gap-2 ${
                  compact
                    ? 'grid-cols-[minmax(6rem,1fr)_4rem_5rem_2fr_auto]'
                    : 'grid-cols-[minmax(6rem,1fr)_5rem_4rem_5rem_2fr_auto]'
                }`}
              >
                <div>
                  <label className={labelCls} htmlFor={`${idPrefix}-slot-${idx}`}>
                    Slot
                  </label>
                  <select
                    id={`${idPrefix}-slot-${idx}`}
                    value={item.slot}
                    onChange={(e) => {
                      const slot = e.target.value as FeedingSlot;
                      updateRow(idx, {
                        slot,
                        ...(slot === 'custom' && !item.time ? { time: '12:00' } : {}),
                      });
                    }}
                    className={inputCls}
                    aria-label={`Meal ${idx + 1} slot`}
                  >
                    {FEEDING_SLOTS.map((s) => (
                      <option key={s} value={s}>
                        {FEEDING_SLOT_LABELS[s]}
                      </option>
                    ))}
                  </select>
                </div>
                {!compact && (
                  <div>
                    <label className={labelCls} htmlFor={`${idPrefix}-time-${idx}`}>
                      Time
                    </label>
                    <input
                      id={`${idPrefix}-time-${idx}`}
                      type="time"
                      value={item.time ?? ''}
                      onChange={(e) =>
                        updateRow(
                          idx,
                          e.target.value ? { time: e.target.value } : { time: undefined },
                        )
                      }
                      className={`${inputCls} num`}
                      aria-label={`Meal ${idx + 1} time`}
                      required={item.slot === 'custom'}
                    />
                  </div>
                )}
                <div>
                  <label className={labelCls} htmlFor={`${idPrefix}-qty-${idx}`}>
                    Qty
                  </label>
                  <input
                    id={`${idPrefix}-qty-${idx}`}
                    type="text"
                    inputMode="decimal"
                    value={item.quantity}
                    onChange={(e) => updateRow(idx, { quantity: e.target.value })}
                    placeholder="1"
                    className={`${inputCls} num`}
                    aria-label={`Meal ${idx + 1} quantity`}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor={`${idPrefix}-unit-${idx}`}>
                    Unit
                  </label>
                  <select
                    id={`${idPrefix}-unit-${idx}`}
                    value={item.unit}
                    onChange={(e) => updateRow(idx, { unit: e.target.value as FeedingUnit })}
                    className={inputCls}
                    aria-label={`Meal ${idx + 1} unit`}
                  >
                    {FEEDING_UNITS.map((u) => (
                      <option key={u} value={u}>
                        {FEEDING_UNIT_LABELS[u]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor={`${idPrefix}-food-${idx}`}>
                    Food
                  </label>
                  <input
                    id={`${idPrefix}-food-${idx}`}
                    type="text"
                    value={item.food}
                    onChange={(e) => updateRow(idx, { food: e.target.value })}
                    placeholder="Royal Canin kibble"
                    className={inputCls}
                    aria-label={`Meal ${idx + 1} food`}
                  />
                </div>
                <div className="flex items-end pb-0.5">
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    aria-label={`Remove meal ${idx + 1}`}
                    className="press rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="size-4" strokeWidth={1.75} />
                  </button>
                </div>
              </div>
              {compact && item.slot === 'custom' && (
                <div className="mt-2 w-32">
                  <label className={labelCls} htmlFor={`${idPrefix}-time-${idx}`}>
                    Time
                  </label>
                  <input
                    id={`${idPrefix}-time-${idx}`}
                    type="time"
                    value={item.time ?? ''}
                    onChange={(e) =>
                      updateRow(
                        idx,
                        e.target.value ? { time: e.target.value } : { time: undefined },
                      )
                    }
                    className={`${inputCls} num`}
                    aria-label={`Meal ${idx + 1} time`}
                  />
                </div>
              )}
              {!compact && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={item.supplements ?? ''}
                    onChange={(e) => updateRow(idx, { supplements: e.target.value || undefined })}
                    placeholder="Supplements (e.g. joint tablet)"
                    className={`${inputCls} text-muted-foreground`}
                    aria-label={`Meal ${idx + 1} supplements`}
                  />
                  <input
                    type="text"
                    value={item.notes ?? ''}
                    onChange={(e) => updateRow(idx, { notes: e.target.value || undefined })}
                    placeholder="Notes (e.g. soak in warm water)"
                    className={`${inputCls} text-muted-foreground`}
                    aria-label={`Meal ${idx + 1} notes`}
                  />
                </div>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            className="press inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary"
          >
            <Plus className="size-3" strokeWidth={2} />
            Add meal
          </button>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.ownerSupplied}
          onChange={(e) => onChange({ ...value, ownerSupplied: e.target.checked })}
          className="size-4"
          aria-label="Owner supplies the food"
        />
        Owner supplies the food
      </label>

      <div className={`grid gap-2 ${compact ? 'grid-cols-1' : 'sm:grid-cols-2'}`}>
        <div>
          <label className={labelCls} htmlFor={`${idPrefix}-allergies`}>
            Allergies
          </label>
          <input
            id={`${idPrefix}-allergies`}
            type="text"
            value={value.allergies}
            onChange={(e) => onChange({ ...value, allergies: e.target.value })}
            placeholder="e.g. chicken, grain"
            className={inputCls}
            aria-label="Feeding allergies"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor={`${idPrefix}-red-flags`}>
            <span className="inline-flex items-center gap-1">
              <AlertTriangle className="size-3" strokeWidth={2} />
              Red flags
            </span>
          </label>
          <input
            id={`${idPrefix}-red-flags`}
            type="text"
            value={value.redFlags}
            onChange={(e) => onChange({ ...value, redFlags: e.target.value })}
            placeholder="e.g. gulps food — use slow feeder"
            className={inputCls}
            aria-label="Feeding red flags"
          />
        </div>
      </div>
    </div>
  );
}
